import React, { useState, useRef } from 'react';
import { 
  Button, 
  Typography, 
  CircularProgress, 
  Box,
  useMediaQuery, 
  useTheme,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  Paper
} from '@mui/material';
import * as XLSX from 'xlsx';
import './App.css';
import translations from './localizations';

function JoinPage({ language }) {
  const [files, setFiles] = useState([]);
  const [processingFiles, setProcessingFiles] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [joinedData, setJoinedData] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef(null);

  const t = (key, params = {}) => {
    const translation = translations[language][key];
    if (typeof translation === 'string') {
      return Object.entries(params).reduce(
        (str, [key, value]) => str.replace(`{${key}}`, value),
        translation
      );
    }
    return key;
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) {
      console.log('No files selected');
      return;
    }
    setFiles(files);
    setJoinedData(null); // Reset joined data when new files are selected
  };

  const handleReset = () => {
    setFiles([]);
    setProcessingFiles(false);
    setJoinedData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const joinFiles = async () => {
    if (files.length === 0) {
      setAlert({
        open: true,
        message: t('noFilesSelected'),
        severity: 'warning'
      });
      return;
    }

    setProcessingFiles(true);
    try {
      let allData = [];

      // Đọc và gộp dữ liệu từ tất cả các files
      for (const file of files) {
        const data = await readExcelFile(file);
        allData = [...allData, ...data];
      }

      // Lưu dữ liệu đã gộp
      setJoinedData(allData);

      setAlert({
        open: true,
        message: t('joinSuccess'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error joining files:', error);
      setAlert({
        open: true,
        message: t('joinError'),
        severity: 'error'
      });
    } finally {
      setProcessingFiles(false);
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  const downloadJoinedFile = () => {
    if (!joinedData) return;

    const ws = XLSX.utils.json_to_sheet(joinedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Joined");
    XLSX.writeFile(wb, "joined_file.xlsx");
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom align="center" style={{ marginTop: '20px' }}>
        {t('joinTitle')}
      </Typography>
      
      <Box className="center-content">
        <div className="button-container">
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="file-upload"
            ref={fileInputRef}
            multiple
          />
          <label htmlFor="file-upload">
            <Button 
              variant="contained" 
              component="span" 
              disabled={processingFiles} 
              className="custom-button"
            >
              {processingFiles ? <CircularProgress size={24} /> : t('uploadButton')}
            </Button>
          </label>
          {files.length > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              className="reset-button custom-button"
            >
              {t('resetButton')}
            </Button>
          )}
        </div>
        
        {files.length > 0 && (
          <Box mt={3} width="100%" maxWidth={600} mx="auto">
            <Typography variant="h6" gutterBottom>
              {t('selectedFiles')}
            </Typography>
            <Paper 
              sx={{ 
                maxHeight: 200, 
                overflow: 'auto',
                marginBottom: 2,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#555',
                  },
                },
              }}
            >
              <List>
                {files.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={`${index + 1}. ${file.name}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>

            <Box mt={2} display="flex" justifyContent="center" gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={joinFiles}
                disabled={processingFiles}
              >
                {processingFiles ? <CircularProgress size={24} /> : t('joinButton')}
              </Button>

              {joinedData && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={downloadJoinedFile}
                >
                  {t('downloadButton')}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default JoinPage;
